import i18n from "./index";
import {
  TRIP_DATE_LIFECYCLE_LABELS,
  OCCUPANCY_LABELS,
  GUIDE_ASSIGNMENT_ROLE_LABELS,
  GUIDE_ASSIGNMENT_STATUS_LABELS,
  GUIDE_AVAILABILITY_REASON_LABELS,
  ORDER_ITEM_KIND_LABELS,
  ENTRY_TASK_KIND_LABELS,
  CALC_CATEGORY_LABELS,
  CALC_SCOPE_LABELS,
  ROOM_TYPE_LABELS,
  INCIDENT_TYPE_LABELS,
  INCIDENT_SEVERITY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  COMPLAINT_TYPE_LABELS,
  COMPLAINT_STATUS_LABELS,
  TRIP_TYPE_LABELS,
  HOTEL_MEAL_PLAN_LABELS,
  HOTEL_DEPARTURE_LABELS,
  HOTEL_FEATURE_LABELS,
  TRIP_STYLE_LABELS,
  DIFFICULTY_LABELS,
  DESTINATION_REGION_LABELS,
  ACTIVITY_TYPE_LABELS,
  ORDER_STATUS_LABELS,
  LEAD_STATUS_LABELS,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_PAYMENT_PROFILE_LABELS,
  SERVICE_KIND_LABELS,
  COST_SCOPE_LABELS,
  BOOKING_MODE_LABELS,
  TRIP_COMPONENT_SEED_TARGET_LABELS,
  SERVICE_BOOKING_STATUS_LABELS,
  FLIGHT_ALLOTMENT_STATUS_LABELS,
  TASK_KIND_LABELS,
  TASK_STATUS_LABELS,
  TASK_ENTITY_TYPE_LABELS,
  INVOICE_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  SUPPLIER_INVOICE_STATUS_LABELS,
  DOCUMENT_KIND_LABELS,
  DOCUMENT_AUDIENCE_LABELS,
  DOCUMENT_STATUS_LABELS,
} from "@ckgo2/shared/types";
import {
  DIFFICULTY_LABELS_CZ,
  HOTEL_DEPARTURE_LABELS_CZ,
  HOTEL_FEATURE_LABELS_CZ,
  HOTEL_MEAL_PLAN_LABELS_CZ,
  TRIP_STYLE_LABELS_CZ,
  TRIP_TYPE_LABELS_CZ,
} from "@ckgo2/shared";
import type {
  TripDateLifecycle,
  OccupancyLevel,
  GuideAssignmentRole,
  GuideAssignmentStatus,
  GuideAvailabilityReason,
  OrderItemKind,
  EntryTaskKind,
  CalcCategory,
  CalcScope,
  RoomType,
  IncidentType,
  IncidentSeverity,
  ExpenseCategory,
  ComplaintType,
  ComplaintStatus,
  DestinationRegion,
  ActivityType,
  OrderStatus,
  LeadStatus,
  SupplierType,
  SupplierPaymentProfile,
  ServiceKind,
  CostScope,
  BookingMode,
  TripComponentSeedTarget,
  ServiceBookingStatus,
  FlightAllotmentStatus,
  TaskKind,
  TaskStatus,
  TaskEntityType,
  InvoiceType,
  InvoiceStatus,
  PaymentMethod,
  SupplierInvoiceStatus,
  DocumentKind,
  DocumentAudience,
  DocumentStatus,
} from "@ckgo2/shared/types";

// Czech mirrors of the shared enum label maps. The shared maps stay English —
// they feed the API and web too — so the admin resolves labels through
// enumLabel() below. Record<Enum, string> enforces completeness: adding an enum
// value upstream without a Czech label here fails typecheck. Enumy sdílené se
// zákazníkem (tripType/tripStyle/difficulty/hotelové) mirror nemají — berou se přímo
// ze sdílených `*_LABELS_CZ`, viz níž.

const CS_TRIP_DATE_LIFECYCLE: Record<TripDateLifecycle, string> = {
  draft: "Koncept",
  tentative: "Předběžný",
  guaranteed: "Garantovaný",
  wont_run: "Nepojede",
  cancelled: "Zrušený",
};

const CS_OCCUPANCY: Record<OccupancyLevel, string> = {
  open: "Volno",
  few_spots: "Poslední místa",
  full: "Obsazeno",
};

const CS_GUIDE_ASSIGNMENT_ROLE: Record<GuideAssignmentRole, string> = {
  lead: "Hlavní průvodce",
  co: "Druhý průvodce",
  backup: "Záskok",
};

const CS_GUIDE_ASSIGNMENT_STATUS: Record<GuideAssignmentStatus, string> = {
  tentative: "Předběžně",
  confirmed: "Potvrzeno",
};

const CS_GUIDE_AVAILABILITY_REASON: Record<GuideAvailabilityReason, string> = {
  vacation: "Dovolená",
  sick: "Nemoc",
  blocked: "Blokováno",
  other: "Jiné",
};

const CS_ORDER_ITEM_KIND: Record<OrderItemKind, string> = {
  trip: "Zájezd",
  custom: "Vlastní položka",
  addon: "Doplněk",
  flight: "Letenka",
  insurance: "Pojištění",
  discount: "Sleva",
  fee: "Poplatek",
};

const CS_ENTRY_TASK_KIND: Record<EntryTaskKind, string> = {
  arrival_card: "Příletová karta",
  visa: "Vízum",
};

const CS_CALC_CATEGORY: Record<CalcCategory, string> = {
  accommodation: "Ubytování",
  transport: "Doprava",
  guide: "Průvodce",
  entrance: "Vstupné",
  flight_domestic: "Vnitrostátní lety",
  insurance: "Pojištění",
  misc: "Ostatní",
};

const CS_CALC_SCOPE: Record<CalcScope, string> = {
  per_pax: "Na osobu",
  per_group: "Na skupinu",
  per_room: "Na pokoj",
};

const CS_ROOM_TYPE: Record<RoomType, string> = {
  single: "Jednolůžkový",
  double: "Dvoulůžkový (manželská postel)",
  twin: "Dvoulůžkový (oddělená lůžka)",
  triple: "Třílůžkový",
  other: "Jiný",
};

const CS_INCIDENT_TYPE: Record<IncidentType, string> = {
  delay: "Zpoždění",
  illness: "Nemoc / zdravotní",
  change: "Změna itineráře",
  complaint: "Stížnost",
  other: "Jiné",
};

const CS_INCIDENT_SEVERITY: Record<IncidentSeverity, string> = {
  low: "Nízká",
  medium: "Střední",
  high: "Vysoká",
};

const CS_EXPENSE_CATEGORY: Record<ExpenseCategory, string> = {
  transport: "Doprava",
  food: "Jídlo a pití",
  accommodation: "Ubytování",
  activity: "Aktivita",
  tips: "Spropitné",
  other: "Ostatní",
};

const CS_COMPLAINT_TYPE: Record<ComplaintType, string> = {
  service_quality: "Kvalita služeb",
  delay: "Zpoždění",
  refund: "Vrácení peněz",
  cancellation: "Storno",
  billing: "Fakturace",
  other: "Jiné",
};

const CS_COMPLAINT_STATUS: Record<ComplaintStatus, string> = {
  open: "Otevřená",
  in_progress: "V řešení",
  resolved: "Vyřešená",
  rejected: "Zamítnutá",
};

// Enumy, jejichž české popisky vidí ZÁROVEŇ zákazník (web, potvrzovací e-maily) —
// mirror by se rozešel s tím, co si klient přečetl, takže se berou přímo ze sdílených
// `*_LABELS_CZ`. Vlastní admin mirror si drží jen enumy, kde se admin formulace liší
// od webové (ACTIVITY_TYPE) nebo web ekvivalent nemá (DESTINATION_REGION).
const CS_TRIP_TYPE = TRIP_TYPE_LABELS_CZ;
const CS_HOTEL_MEAL_PLAN = HOTEL_MEAL_PLAN_LABELS_CZ;
// Keyed by the CUSTOMER choices (incl. "any"), matching the shared EN map — a lead can carry
// departure "any" and enumLabel() would otherwise print the raw value.
const CS_HOTEL_DEPARTURE = HOTEL_DEPARTURE_LABELS_CZ;
const CS_HOTEL_FEATURE = HOTEL_FEATURE_LABELS_CZ;
const CS_TRIP_STYLE = TRIP_STYLE_LABELS_CZ;
const CS_DIFFICULTY = DIFFICULTY_LABELS_CZ;

const CS_DESTINATION_REGION: Record<DestinationRegion, string> = {
  northern: "Severní Vietnam",
  central: "Střední Vietnam",
  southern: "Jižní Vietnam",
};

const CS_ACTIVITY_TYPE: Record<ActivityType, string> = {
  water: "Vodní sporty a plavby",
  adventure: "Dobrodružství a treking",
  culture: "Kultura a památky",
  nature: "Příroda a zvířata",
  food: "Gastronomie",
  wellness: "Wellness a relaxace",
};

const CS_ORDER_STATUS: Record<OrderStatus, string> = {
  inquiry: "Poptávka",
  quoted: "Nabídnuto",
  confirmed: "Potvrzeno",
  deposit_paid: "Záloha zaplacena",
  fully_paid: "Plně zaplaceno",
  completed: "Dokončeno",
  cancelled: "Stornováno",
};

const CS_LEAD_STATUS: Record<LeadStatus, string> = {
  received: "Přijato",
  quoted: "Nabídnuto",
  call_back: "Zavolat zpět",
  dormant: "Spící",
  sold: "Prodáno",
  lost: "Ztraceno",
};

const CS_SUPPLIER_TYPE: Record<SupplierType, string> = {
  hotel: "Hotel",
  airline: "Letecká společnost",
  insurance: "Pojišťovna",
  transport: "Doprava",
  guide: "Místní průvodce",
  activity: "Poskytovatel aktivit",
  visa: "Vízový agent",
  other: "Jiné",
};

const CS_SUPPLIER_PAYMENT_PROFILE: Record<SupplierPaymentProfile, string> = {
  invoice: "Faktura (splatnost)",
  cash_offline: "Hotově na místě",
  online: "Online / kartou",
  prepaid: "Předplaceno",
};

const CS_SERVICE_KIND: Record<ServiceKind, string> = {
  flight: "Letenka",
  hotel: "Hotel",
  insurance: "Pojištění",
  activity: "Aktivita",
  visa: "Vízum",
  transfer: "Transfer",
  other: "Jiné",
};

const CS_COST_SCOPE: Record<CostScope, string> = {
  per_departure: "Na termín (fixní)",
  per_pax: "Na osobu",
};

const CS_BOOKING_MODE: Record<BookingMode, string> = {
  confirm_required: "Potvrdit u dodavatele",
  on_demand: "Na vyžádání — vlastní / vždy dostupné",
};

const CS_TRIP_COMPONENT_SEED_TARGET: Record<TripComponentSeedTarget, string> = {
  order: "Na objednávku",
  departure: "Na termín (sdílené)",
};

const CS_SERVICE_BOOKING_STATUS: Record<ServiceBookingStatus, string> = {
  draft: "Koncept",
  requested: "Poptáno",
  confirmed: "Potvrzeno",
  paid: "Zaplaceno",
  cancelled: "Zrušeno",
};

const CS_FLIGHT_ALLOTMENT_STATUS: Record<FlightAllotmentStatus, string> = {
  active: "Aktivní",
  released: "Uvolněno",
  ticketed: "Vystaveno",
  cancelled: "Zrušeno",
};

const CS_TASK_KIND: Record<TaskKind, string> = {
  readiness: "Připravenost",
  deadline: "Deadline",
  callback: "Zavolat zpět",
  manual: "Úkol",
  supplier_followup: "Urgence dodavatele",
};

const CS_TASK_STATUS: Record<TaskStatus, string> = {
  open: "Otevřený",
  done: "Hotovo",
  snoozed: "Odloženo",
  cancelled: "Zrušeno",
};

const CS_TASK_ENTITY_TYPE: Record<TaskEntityType, string> = {
  order: "Objednávka",
  lead: "Poptávka",
  service_booking: "Služba",
  departure: "Termín",
  customer: "Zákazník",
  invoice: "Faktura",
  flight_allotment: "Allotment letenek",
};

const CS_INVOICE_TYPE: Record<InvoiceType, string> = {
  proforma: "Proforma",
  final: "Konečná faktura",
  insurance: "Pojištění",
  credit_note: "Dobropis",
};

const CS_INVOICE_STATUS: Record<InvoiceStatus, string> = {
  issued: "Vystavená",
  paid: "Zaplacená",
  cancelled: "Stornovaná",
};

const CS_PAYMENT_METHOD: Record<PaymentMethod, string> = {
  bank: "Bankovní převod",
  cash: "Hotovost",
  stripe: "Karta (Stripe)",
  qr: "QR platba",
  other: "Jiné",
};

const CS_SUPPLIER_INVOICE_STATUS: Record<SupplierInvoiceStatus, string> = {
  received: "Přijatá",
  approved: "Schválená",
  paid: "Zaplacená",
  disputed: "Rozporovaná",
};

const CS_DOCUMENT_KIND: Record<DocumentKind, string> = {
  contract: "Smlouva",
  voucher: "Voucher",
  itinerary: "Itinerář",
  package: "Cestovní balíček",
  supplier_confirmation: "Potvrzení dodavatele",
  offer: "Nabídka",
  confirmation: "Potvrzení rezervace",
  insurance: "Pojistka",
  other: "Jiné",
};

const CS_DOCUMENT_AUDIENCE: Record<DocumentAudience, string> = {
  client: "Zákazník",
  guide: "Průvodce",
  public: "Veřejné",
  internal: "Interní",
};

const CS_DOCUMENT_STATUS: Record<DocumentStatus, string> = {
  draft: "Koncept",
  sent: "Odesláno",
  viewed: "Zobrazeno",
  signed: "Podepsáno",
  void: "Zneplatněno",
};

// Registry keyed by the shared EN map object itself, so call sites stay a
// mechanical rewrite: ORDER_STATUS_LABELS[x] → enumLabel(ORDER_STATUS_LABELS, x).
// CONTINENT_LABELS is deliberately absent — its shared values are already Czech.
const CS_BY_MAP = new Map<object, Record<string, string>>([
  [TRIP_DATE_LIFECYCLE_LABELS, CS_TRIP_DATE_LIFECYCLE],
  [OCCUPANCY_LABELS, CS_OCCUPANCY],
  [GUIDE_ASSIGNMENT_ROLE_LABELS, CS_GUIDE_ASSIGNMENT_ROLE],
  [GUIDE_ASSIGNMENT_STATUS_LABELS, CS_GUIDE_ASSIGNMENT_STATUS],
  [GUIDE_AVAILABILITY_REASON_LABELS, CS_GUIDE_AVAILABILITY_REASON],
  [ORDER_ITEM_KIND_LABELS, CS_ORDER_ITEM_KIND],
  [ENTRY_TASK_KIND_LABELS, CS_ENTRY_TASK_KIND],
  [CALC_CATEGORY_LABELS, CS_CALC_CATEGORY],
  [CALC_SCOPE_LABELS, CS_CALC_SCOPE],
  [ROOM_TYPE_LABELS, CS_ROOM_TYPE],
  [INCIDENT_TYPE_LABELS, CS_INCIDENT_TYPE],
  [INCIDENT_SEVERITY_LABELS, CS_INCIDENT_SEVERITY],
  [EXPENSE_CATEGORY_LABELS, CS_EXPENSE_CATEGORY],
  [COMPLAINT_TYPE_LABELS, CS_COMPLAINT_TYPE],
  [COMPLAINT_STATUS_LABELS, CS_COMPLAINT_STATUS],
  [TRIP_TYPE_LABELS, CS_TRIP_TYPE],
  [HOTEL_MEAL_PLAN_LABELS, CS_HOTEL_MEAL_PLAN],
  [HOTEL_DEPARTURE_LABELS, CS_HOTEL_DEPARTURE],
  [HOTEL_FEATURE_LABELS, CS_HOTEL_FEATURE],
  [TRIP_STYLE_LABELS, CS_TRIP_STYLE],
  [DIFFICULTY_LABELS, CS_DIFFICULTY],
  [DESTINATION_REGION_LABELS, CS_DESTINATION_REGION],
  [ACTIVITY_TYPE_LABELS, CS_ACTIVITY_TYPE],
  [ORDER_STATUS_LABELS, CS_ORDER_STATUS],
  [LEAD_STATUS_LABELS, CS_LEAD_STATUS],
  [SUPPLIER_TYPE_LABELS, CS_SUPPLIER_TYPE],
  [SUPPLIER_PAYMENT_PROFILE_LABELS, CS_SUPPLIER_PAYMENT_PROFILE],
  [SERVICE_KIND_LABELS, CS_SERVICE_KIND],
  [COST_SCOPE_LABELS, CS_COST_SCOPE],
  [BOOKING_MODE_LABELS, CS_BOOKING_MODE],
  [TRIP_COMPONENT_SEED_TARGET_LABELS, CS_TRIP_COMPONENT_SEED_TARGET],
  [SERVICE_BOOKING_STATUS_LABELS, CS_SERVICE_BOOKING_STATUS],
  [FLIGHT_ALLOTMENT_STATUS_LABELS, CS_FLIGHT_ALLOTMENT_STATUS],
  [TASK_KIND_LABELS, CS_TASK_KIND],
  [TASK_STATUS_LABELS, CS_TASK_STATUS],
  [TASK_ENTITY_TYPE_LABELS, CS_TASK_ENTITY_TYPE],
  [INVOICE_TYPE_LABELS, CS_INVOICE_TYPE],
  [INVOICE_STATUS_LABELS, CS_INVOICE_STATUS],
  [PAYMENT_METHOD_LABELS, CS_PAYMENT_METHOD],
  [SUPPLIER_INVOICE_STATUS_LABELS, CS_SUPPLIER_INVOICE_STATUS],
  [DOCUMENT_KIND_LABELS, CS_DOCUMENT_KIND],
  [DOCUMENT_AUDIENCE_LABELS, CS_DOCUMENT_AUDIENCE],
  [DOCUMENT_STATUS_LABELS, CS_DOCUMENT_STATUS],
]);

/**
 * Language-aware label for a shared enum value: English straight from the shared
 * map, Czech from the mirror registered above. Unregistered maps fall back to
 * English rather than throwing — safe for maps that are already Czech upstream.
 */
export function enumLabel<K extends string>(enMap: Record<K, string>, value: K): string {
  if (i18n.language === "cs") {
    const csMap = CS_BY_MAP.get(enMap) as Record<K, string> | undefined;
    const label = csMap?.[value];
    if (label) return label;
  }
  return enMap[value] ?? value;
}
